import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import TimePicker from '../shared/TimePicker';
import Switch from '../shared/Switch';
import { color } from '../../app/theme';
import { setData } from '../../features/mainSlice';

const useStyles = makeStyles(
  theme => ({
    root: {},
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 12,
    },
    timeSetting: {
      '& > div': {
        display: 'inline-block',
      },
      '& > div:last-child': {
        [theme.breakpoints.down('md')]: {
          display: 'block',
          marginTop: 8,
        },
      },
    },
    date: {
      width: 180,
      marginRight: 8,
    },
    time: {
      width: 100,
    },
    caterpillar: {
      fontSize: 13,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      letterSpacing: 'normal',
      color: color.$3,
      margin: '0 8px',
    },
  }),
  {
    name: 'Sleep',
  },
);

const Sleep = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { oos } = useSelector(state => state.ma?.data);
  const { isActive } = useSelector(store => store.ma);
  const { enabled: isSleep, hour, minute, duration } = oos || {};

  const [startTime, setStartTime] = useState(moment());
  const [endTime, setEndTime] = useState(moment());

  const onChange = (date, type) => {
    if (type === 'start') {
      const start = moment()
        .hour(moment(date).hours())
        .minute(moment(date).minute());
      const end = moment()
        .hour(moment(endTime).hours())
        .minute(moment(endTime).minute());

      if (moment(start).isSame(end, 'minute')) {
        end.add(1, 'minute');
      }
      dispatch(
        setData({
          oos: {
            ...oos,
            hour: moment(start).hours(),
            minute: moment(start).minute(),
            duration:
              end.diff(moment(start), 'minute') >= 0
                ? end.diff(moment(start), 'minute') * 60
                : end.add(1, 'days').diff(moment(start), 'minute') * 60,
          },
        }),
      );
    }

    if (type === 'end') {
      const start = moment()
        .hour(moment(startTime).hours())
        .minute(moment(startTime).minute());
      const end = moment()
        .hour(moment(date).hours())
        .minute(moment(date).minute());

      if (moment(start).isSame(end, 'minute')) {
        end.add(1, 'minute');
      }
      dispatch(
        setData({
          oos: {
            ...oos,
            duration:
              end.diff(moment(start), 'minute') >= 0
                ? end.diff(moment(start), 'minute') * 60
                : end.add(1, 'days').diff(moment(start), 'minute') * 60,
          },
        }),
      );
    }
  };

  const onSwitch = e => {
    dispatch(
      setData({
        oos: {
          ...oos,
          enabled: e.target.checked,
        },
      }),
    );
  };

  useEffect(() => {
    setStartTime(moment().hour(hour).minute(minute));
    setEndTime(moment().hour(hour).minute(minute).add(duration, 'seconds'));
  }, [hour, minute, duration]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('setting.sleep')}</div>
      <div className={classes.subtitle}>{t('setting.sleep-subtitle')}</div>
      <div className={classes.timeSetting}>
        <div>
          <div style={{ display: 'flex' }}>
            <TimePicker
              className={classes.time}
              selectedTime={startTime}
              onChange={value => {
                onChange(value, 'start');
              }}
              placeholder={t('date-placeholder')}
              isUp
              disabled={!isSleep || isActive}
            />
          </div>
        </div>
        <div className={classes.caterpillar}>~</div>
        <div>
          <div style={{ display: 'flex' }}>
            <TimePicker
              className={classes.time}
              selectedTime={endTime}
              onChange={value => {
                onChange(value, 'end');
              }}
              placeholder={t('time-placeholder')}
              isUp
              disabled={!isSleep || isActive}
            />
          </div>
        </div>
        <Switch checked={isSleep} onChange={onSwitch} disabled={isActive} />
      </div>
    </div>
  );
};

export default Sleep;
